import React, { useState, useEffect, useCallback, useRef } from "react";
import dayjs from "dayjs";

import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  Tabs,
  Tab,
  Typography,
  Drawer,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from "@mui/material";
import {
  themes,
  parentBoxStyle,
  doubleArrowStyle,
  cardBorder,
  tableRowSize,
  editIconStyle,
  childBoxStyleSpaceBetween,
} from "services/constants";

import {
  Check,
  ArrowRight,
  DoubleArrow,
  Edit,
  InfoOutlined,
} from "@mui/icons-material";
import { OffDayPostApproval } from "./OffDayPostApproval";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CustomTextBox } from "components/CustomComponents";
import { CustomButtonWithBorder } from "components/CustomComponents/CustomButton";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import sessionHandling from "services/utils/notificationUtils";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { tooltipStyleProps } from "services/constants/Styles";
import { EditMultipleCheckInOut } from "./EditMultipleCheckInOut";
import { Loader } from "components/Loader";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";
import { useForm } from "react-hook-form";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { onlyNumberRegex, rgxWithDot } from "services/constants/patterns";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { PunchEntries } from "./PunchEntries";
import { checkEditorPermissionAny } from "services/utils/HelperFunction";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { designColors, panelDrawerSx } from "services/constants/designTokens";

const theme = createTheme({
  palette: {
    primary: {
      main: "#00D4AA",
    },
    secondary: {
      main: "#6c757d",
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

// A "Missing" day has an unpaired punch, so the Punch Entries tab is the
// useful one to land on. The status name differs by caller: card rows send
// card_display_status / type, the timeline sends type, and the loaded record
// carries attendance_status.
const isMissingStatus = (...values) =>
  values.some((value) => String(value ?? "").trim().toLowerCase() === "missing");

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 1 }}>{children}</Box>}
    </div>
  );
}

// The shift start or end can come from an approved request merged into the
// day instead of a punch (`*_merge`, e.g. On Duty); each is listed as its own
// row in the shift timing.
const MERGE_LABELS = { on_duty: "On Duty" };

const mergeLabel = (type) =>
  MERGE_LABELS[type] ||
  String(type ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

// Merged sessions behind the shift start / end; one request covering both
// ends is listed once.
const mergedSessions = (details) => {
  const shift = details?.shift_data ?? {};
  const merges = [
    shift.shift_start_merge ?? details?.check_in_merge,
    shift.shift_end_merge ?? details?.check_out_merge,
  ].filter(Boolean);
  return merges.filter(
    (merge, index) =>
      merges.findIndex(
        (other) =>
          other.type === merge.type && other.approval_id === merge.approval_id,
      ) === index,
  );
};

// Fixed deductions (e.g. a mandatory break) have no start or end time.
const isTimelessBreak = (item) => !item?.break_start && !item?.break_end;

const InfoRow = ({ label, value, tooltip }) => (
  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontWeight: 600, color: "#757575" }}>
        {label}
      </Typography>

      {tooltip && (
        <Tooltip title={tooltip} arrow slotProps={tooltipStyleProps}>
          <InfoOutlined
            sx={{ fontSize: 18, color: "#9e9e9e", cursor: "pointer" }}
          />
        </Tooltip>
      )}
    </Box>

    <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
  </Box>
);

export const EditCheckInOut = ({
  setOpenSidePeek,
  openSidePeek,
  shiftDetails,
  setEditState,
  departmentId,
  payrollEdit = false,
  reCallApiOffDayDuty = false,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const [tabValue, setTabValue] = useState(() =>
    isMissingStatus(
      shiftDetails?.card_display_status,
      shiftDetails?.type,
      shiftDetails?.attendance_status,
      shiftDetails?.status,
    )
      ? 1
      : 0,
  );
  const defaultTabResolved = useRef(false);
  const [doAnyAct, setDoAnyAct] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [attendanceDetails, setAttendanceDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recallApi, setRecallApi] = useState(true);
  const [editDetail, setEditDetail] = useState(false);
  const [isEditRecord, setIsEditRecord] = useState(false);
  const [url, setUrl] = useState(null);
  const [sessionDetails, setSessionDetails] = useState("");
  const [isUpdate, setIsUpdate] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [getUpdatedData, setGetUpdatedData] = useState(false);
  const [defaultBandRows, setDefaultBandRows] = useState([]);
  const [bandRows, setBandRows] = useState([]);
  const [editingBandId, setEditingBandId] = useState(null);
  const [bandRowErrors, setBandRowErrors] = useState({});
  const [savingBandId, setSavingBandId] = useState(null);
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      pay_cycle: "",
      payout_policy: "",
      reason: "",
      button_loader: false,
    },
  });

  useEffect(() => {
    if (attendanceDetails?.date && attendanceDetails?.user_id) {
      setUrl(
        `${domain}Team_Zone/Approve/Over_time/ot-post-approve/?selected_user_id=${
          attendanceDetails?.user_id
        }&ot_date=${attendanceDetails?.date.split("-").reverse().join("-")}&company_id=${requestCompanyId}`,
      );
    }
  }, [attendanceDetails, domain, requestCompanyId]);

  const { result: otSessionData, setLoad: reCallApi } = useFetch(url);

  useEffect(() => {
    if (getUpdatedData) {
      reCallApi(true);
      setGetUpdatedData(false);
    }
  }, [getUpdatedData, reCallApi, recallApi]);

  // This modal is opened from Employee View, Department View, Shift View
  // (ListView/CardView) and Timeline View alike with no prop indicating
  // which one -- "Status", the combined submodule all four used to share,
  // no longer exists in the live payload (it split into four standalone
  // submodules), so grant the edit action if any one of them has
  // editor:true for this role instead of a single lookup that can never
  // match again.
  let check_access = payrollEdit
    ? true
    : checkEditorPermissionAny(userScreenAccess?.module_details, "Attendance", [
        "Employee View",
        "Department View",
        "Shift View",
        "Timeline View",
      ]);

  const preShiftData =
    attendanceDetails?.ot_allowed &&
    (attendanceDetails?.ot_consideration === "Both" ||
      attendanceDetails?.ot_consideration === "PreOT");

  const postShiftData =
    attendanceDetails?.ot_allowed &&
    (attendanceDetails?.ot_consideration === "Both" ||
      attendanceDetails?.ot_consideration === "PostOT");

  const handleTabChange = (event, newValue) => {
    defaultTabResolved.current = true;
    setTabValue(newValue);
  };

  // Callers that do not pass a status (reports, approvals, payroll) still get
  // the Punch Entries default once the record itself says "Missing". Runs
  // once per open, and never after the user has picked a tab themselves.
  useEffect(() => {
    if (defaultTabResolved.current || !attendanceDetails?.attendance_status) {
      return;
    }
    defaultTabResolved.current = true;
    if (isMissingStatus(attendanceDetails.attendance_status)) setTabValue(1);
  }, [attendanceDetails?.attendance_status]);

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Mui.Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpenSidePeek(open);
    if (open === false) {
      if (doAnyAct) {
        setDoAnyAct(false);
      }
    }
  };

  const handleToggle = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  useEffect(() => {
    const fetchLeaveGroupDatas = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${domain}attendance_manual_entry/?user_id=${
            shiftDetails?.user_id
          }&date=${shiftDetails?.date?.split("-").reverse().join("-")}&company_id=${requestCompanyId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setAttendanceDetails(res);
          setRecallApi(false);
          setLoading(false);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error('REQUEST_FAILED');
        }
      } catch (error) {
        console.error('[Index] operation failed:', error);
        showToast("error", "Couldn't load attendance records. Please refresh the page.");
        if (
          error.message ===
          "Editing entries before 25-Mar-2025 is not supported."
        )
          setRecallApi(false);
        setLoading(false);
      }
    };
    if (recallApi) {
      fetchLeaveGroupDatas();
    }
  }, [domain, recallApi, shiftDetails?.date, shiftDetails?.user_id, token, requestCompanyId]);

  const buildBandRows = useCallback((bands = []) => {
    const normalizeBandHours = (value) => {
      if (!value || typeof value !== "string") return "";

      const timeParts = value.split(":");
      if (timeParts.length < 2) return value;

      return `${timeParts[0]}:${timeParts[1]}`;
    };

    if (!Array.isArray(bands)) return [];

    return bands.map((band, index) => ({
      id: band?.id ?? index,
      band_name: band?.band_name ?? `band ${index + 1}`,
      band_factor:
        band?.band_factor === null || band?.band_factor === undefined
          ? ""
          : String(band?.band_factor),
      band_hours: normalizeBandHours(
        band?.final_approved_durations ?? band?.band_hours ?? band?.hours ?? "",
      ),
    }));
  }, []);

  const fetchApprovalBandRows = useCallback(
    async (approvalId) => {
      if (!domain || !approvalId) {
        setDefaultBandRows([]);
        setBandRows([]);
        setEditingBandId(null);
        return;
      }

      const response = await customGetAPI(
        `${domain}My_Zone/Apply/Over_time/attendance-band-factor-overtime/?post_ot_approval_id=${approvalId}&company_id=${requestCompanyId}`,
      );

      if (response?.ok) {
        const rows = buildBandRows(response?.responseDetails ?? []);
        setDefaultBandRows(rows);
        setBandRows(rows);
        setEditingBandId(null);
        setBandRowErrors({});
        setSavingBandId(null);
      } else {
        setDefaultBandRows([]);
        setBandRows([]);
        setEditingBandId(null);
        setBandRowErrors({});
        setSavingBandId(null);
      }
    },
    [buildBandRows, domain, requestCompanyId],
  );

  const resetApprovalFormValues = useCallback(
    (session) => {
      if (!session) {
        reset();
        return;
      }

      const formValues = {
        pay_cycle: "",
        payout_policy: session?.payout_policy ?? "",
        reason: "",
        button_loader: false,
        durationHrs: null,
        no_of_shifts: "",
        amount: "",
      };

      if (
        session?.ot_calculation_based_on === "Hours" ||
        session?.ot_calculation_based_on === "Equivalent Hours"
      ) {
        const qtyValue = session?.approved_qty || session?.credited_qty;

        formValues.durationHrs =
          qtyValue && dayjs(qtyValue, "HH:mm", true).isValid()
            ? dayjs(qtyValue, "HH:mm", true)
            : null;
      } else if (session?.ot_calculation_based_on === "Shift") {
        formValues.no_of_shifts =
          session?.approved_qty ?? session?.credited_qty;
      } else {
        const qtyValue = session?.approved_qty ?? session?.credited_qty;
        formValues.amount =
          qtyValue === null || qtyValue === undefined ? "" : parseInt(qtyValue);
      }

      reset(formValues);
    },
    [reset],
  );

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}Team_Zone/Approve/Over_time/ot-post-approve/?company_id=${requestCompanyId}`,
      "PUT",
      {
        approval_id: sessionDetails?.approval_id,
        payout_policy: data?.payout_policy,
        status: "Approved",
        ...((sessionDetails?.ot_calculation_based_on === "Hours" ||
          sessionDetails?.ot_calculation_based_on === "Equivalent Hours") && {
          approved_qty: dayjs(data?.durationHrs).format("HH:mm"),
        }),
        ...(sessionDetails?.ot_calculation_based_on === "Shift" && {
          approved_qty: data?.no_of_shifts,
        }),
        ...(sessionDetails?.ot_calculation_based_on === "Amount" && {
          approved_qty: data?.amount,
        }),
      },
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setIsUpdate(false);
      setOpenSidePeek(false);
      setEditState(true);
      reCallApi(true);
      setValue("button_loader", false);
    } else {
      showToast("error", response?.message);
      setValue("button_loader", false);
    }
  };

  const handleReject = async () => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}Team_Zone/Approve/Over_time/ot-post-approve/?company_id=${requestCompanyId}`,
      "PUT",
      {
        approval_id: sessionDetails?.approval_id,
        status: "Rejected",
        rejected_reason: watch("reason"),
      },
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setIsUpdate(false);
      setOpenSidePeek(false);
      setEditState(true);
      setValue("button_loader", false);
      reCallApi(true);
    } else {
      showToast("error", response?.message);
      setValue("button_loader", false);
    }
  };

  useEffect(() => {
    if (sessionDetails && isUpdate) {
      resetApprovalFormValues(sessionDetails);
      setIsUpdate(false);
    }
  }, [isUpdate, resetApprovalFormValues, sessionDetails]);

  useEffect(() => {
    if (!sessionDetails?.approval_id || !Array.isArray(otSessionData)) return;

    const refreshedSession = otSessionData.find(
      (session) => session?.approval_id === sessionDetails?.approval_id,
    );

    if (!refreshedSession) return;

    setSessionDetails(refreshedSession);
    resetApprovalFormValues(refreshedSession);
  }, [otSessionData, resetApprovalFormValues, sessionDetails?.approval_id]);

  useEffect(() => {
    fetchApprovalBandRows(sessionDetails?.approval_id);
  }, [fetchApprovalBandRows, sessionDetails?.approval_id]);

  const updateNumberOfShifs = (event) => {
    if (event.target.value && rgxWithDot.test(event.target.value)) {
      setValue("no_of_shifts", event.target.value);
    } else {
      setValue("no_of_shifts", "");
    }
  };

  const updateAmount = (event) => {
    if (event.target.value && onlyNumberRegex.test(event.target.value)) {
      setValue("amount", event.target.value);
    } else {
      setValue("amount", "");
    }
  };

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
    resetApprovalFormValues(sessionDetails);
  };

  const updateBandHours = (bandId, value) => {
    setBandRows((prev) =>
      prev.map((row) =>
        row.id === bandId
          ? {
              ...row,
              band_hours: value,
            }
          : row,
      ),
    );
    setBandRowErrors((prev) => ({
      ...prev,
      [bandId]: {
        ...prev[bandId],
        band_hours: value ? "" : prev[bandId]?.band_hours,
      },
    }));
  };

  const validateBandRow = (band) => {
    const nextErrors = {
      band_hours: band?.band_hours ? "" : "Value is required",
    };

    setBandRowErrors((prev) => ({
      ...prev,
      [band.id]: nextErrors,
    }));

    return !nextErrors.band_hours;
  };

  const handleBandEditToggle = async (band) => {
    if (editingBandId === band.id) {
      if (!validateBandRow(band)) return;

      setSavingBandId(band.id);
      const approvedDuration = band.band_hours ? `${band.band_hours}:00` : "";
      const response = await customAPI(
        `${domain}My_Zone/Apply/Over_time/attendance-band-factor-overtime/${band.id}/?company_id=${requestCompanyId}`,
        "PUT",
        {
          band_factor: parseFloat(band.band_factor),
          final_approved_durations: approvedDuration,
          post_ot_approval: sessionDetails?.approval_id,
        },
      );

      if (response?.ok) {
        await fetchApprovalBandRows(sessionDetails?.approval_id);
        reCallApi(true);
        showToast("success", response?.responseDetails?.message);
        setEditingBandId(null);
      } else {
        setSavingBandId(null);
      }

      return;
    }

    setBandRowErrors((prev) => ({
      ...prev,
      [band.id]: {
        band_hours: "",
      },
    }));
    setEditingBandId(band.id);
  };

  const handleReason = (event) => {
    const reason = event.target.value;
    if (reason) {
      setValue("reason", event.target.value);
    } else {
      setValue("reason", "");
    }
  };

  const headerWrapperSx = {
    bgcolor: themes?.primary,
    borderBottom: "1px solid #c8cbd3",
    borderRadius: "5px 5px 0 0",
  };

  const headerGridSx = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    p: 1,
  };

  const headerTextSx = {
    fontWeight: 600,
    textAlign: "center",
  };

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <Drawer
          anchor="right"
          open={openSidePeek}
          onClose={toggleDrawer("right", false)}
          sx={panelDrawerSx("680px")}
        >
          <Box sx={{ ...parentBoxStyle, height: "100%", minHeight: 0 }}>
            <Paper
              elevation={0}
              sx={{
                borderBottom: `1px solid ${themes?.miliedLightGray}`,
                borderRadius: 0,
              }}
            >
              <Box sx={childBoxStyleSpaceBetween}>
                <IconButton
                  onClick={() => setOpenSidePeek(false)}
                  sx={doubleArrowStyle}
                >
                  <DoubleArrow />
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip
                    title={
                      check_access
                        ? "Edit Record"
                        : "Access denied: You are not authorized to edit attendance details"
                    }
                    arrow
                    slotProps={tooltipStyleProps}
                  >
                    <span>
                      <IconButton
                        onClick={() => {
                          setEditDetail(true);
                          setIsEditRecord(true);
                          setTabValue(0);
                          setExpandedIndex(null);
                        }}
                        disabled={!check_access}
                        sx={editIconStyle}
                      >
                        <Edit />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ ml: 2.2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Attendance Details
                </Typography>
                <Typography variant="body2" color={designColors.slate600}>
                  View employee shift details, hours worked, and overtime for
                  payroll.
                </Typography>
              </Box>
            </Paper>

            <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
              <Box>
                <UserDetailsRow
                  userDetails={attendanceDetails}
                  isEditRecord={isEditRecord}
                />
                <Box>
                  <Tabs
                    selectionFollowsFocus
                    textColor={themes.primaryIndicator}
                    TabIndicatorProps={{
                      style: {
                        backgroundColor: themes.primaryIndicator,
                      },
                    }}
                    value={tabValue}
                    onChange={handleTabChange}
                    centered
                    variant="fullWidth"
                  >
                    <Tab
                      aria-label="phone"
                      sx={{
                        "&.Mui-selected": {
                          color: "#09bd71",
                        },
                        "&:not(.Mui-selected)": {
                          backgroundColor: themes.shadowBackGround,
                        },
                      }}
                      label={
                        <Typography
                          sx={{
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          Shift Map
                        </Typography>
                      }
                      iconPosition="end"
                    />
                    <Tab
                      aria-label="phone"
                      sx={{
                        "&.Mui-selected": {
                          color: "#09bd71",
                        },
                        "&:not(.Mui-selected)": {
                          backgroundColor: themes.shadowBackGround,
                        },
                      }}
                      label={
                        <Typography
                          sx={{
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          Punch Entries
                        </Typography>
                      }
                      iconPosition="end"
                    />
                  </Tabs>

                  {loading ? (
                    <Loader />
                  ) : (
                    <TabPanel value={tabValue} index={0}>
                      <ConfirmationDialog
                        open={showConfirmationDialog}
                        onClose={handleCancelClose}
                        onConfirm={() => {
                          handleReject();
                        }}
                        title={"Reason"}
                        confirmText={"Reject"}
                        cancelText="Cancel"
                        showChildren={true}
                      >
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                            Reason (Optional)
                          </Typography>
                          {showConfirmationDialog && (
                            <CustomTextBox
                              type="multiline"
                              rules={{
                                ...register("reason", {
                                  required: false,
                                }),
                              }}
                              selectedValue={watch("reason")}
                              updateDataFunction={handleReason}
                              placeHolder={"Enter a reason for deletion"}
                            />
                          )}
                        </Box>
                      </ConfirmationDialog>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Timing
                        </Typography>
                        {preShiftData &&
                          attendanceDetails?.pre_ot_data?.length > 0 && (
                            <Box sx={{ spaceY: 1 }}>
                              <Box sx={{ mb: 2, border: "1px solid #c8cbd3" }}>
                                {attendanceDetails?.pre_ot_data?.length > 0 && (
                                  <Box sx={headerWrapperSx}>
                                    <Box sx={headerGridSx}>
                                      <Typography />
                                      <Typography sx={headerTextSx}>
                                        Start
                                      </Typography>
                                      <Typography sx={headerTextSx}>
                                        End
                                      </Typography>
                                      <Typography sx={headerTextSx}>
                                        Duration
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                <Box sx={{ borderBottom: "1px solid #c8cbd3" }}>
                                  {attendanceDetails?.pre_ot_data?.map(
                                    (row, index) => (
                                      <Box
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "1fr 1fr 1fr 1fr",
                                          p: 1,
                                          borderBottom: "1px solid #c8cbd3",
                                        }}
                                      >
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          OT Session 1
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            textAlign: "center",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {row?.ot_start_time ?? "00:00"}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            textAlign: "center",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {row?.ot_end_time ?? "00:00"}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            textAlign: "center",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {row?.ot_duration ?? "00:00"}
                                        </Typography>
                                      </Box>
                                    ),
                                  )}
                                  {/* Break */}
                                  {attendanceDetails?.pre_ot_data?.[0]?.break_data?.map(
                                    (item, index) => (
                                      <Box
                                        key={index}
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "1fr 1fr 1fr 1fr",
                                          p: 1,
                                          bgcolor: "rgba(234, 171, 53, 0.1)",
                                        }}
                                      >
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          Break
                                        </Typography>

                                        <Typography
                                          variant="body2"
                                          sx={{ textAlign: "center" }}
                                        >
                                          {item.break_start ?? "00:00"}
                                        </Typography>

                                        <Typography
                                          variant="body2"
                                          sx={{ textAlign: "center" }}
                                        >
                                          {item.break_end ?? "00:00"}
                                        </Typography>

                                        <Typography
                                          variant="body2"
                                          sx={{ textAlign: "center" }}
                                        >
                                          {item.break_duration ?? "00:00"}
                                          {item?.is_cancelled ? " (Cancelled)" : ""}
                                        </Typography>
                                      </Box>
                                    ),
                                  )}
                                </Box>

                                <Box
                                  sx={{
                                    bgcolor: themes?.primary,
                                    p: 1,
                                    borderRadius: "0 0 5px 5px",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Typography variant="body1">
                                        Duration
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {attendanceDetails?.pre_ot_data[0]
                                          ?.ot_duration ?? "00:00"}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Typography variant="body1">
                                        Worked
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {attendanceDetails?.pre_ot_data[0]
                                          ?.ot_worked_duration ?? "00:00"}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{ display: "flex", gap: 1, mr: 1.5 }}
                                    >
                                      <Typography variant="body1">
                                        Break
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {attendanceDetails?.pre_ot_data[0]
                                          ?.break_duration ?? "00:00"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          )}

                        <Box sx={{ mb: 2, border: "1px solid #c8cbd3" }}>
                          <Box sx={headerWrapperSx}>
                            <Box sx={headerGridSx}>
                              <Typography />
                              <Typography sx={headerTextSx}>Start</Typography>
                              <Typography sx={headerTextSx}>End</Typography>
                              <Typography sx={headerTextSx}>
                                Duration
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              borderBottom: "1px solid #c8cbd3",
                              borderRadius: "5px 5px 0 0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                p: 1,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                              >
                                Shift
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  textAlign: "center",
                                  fontWeight: 600,
                                }}
                              >
                                {attendanceDetails?.shift_in_time ?? "-"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  textAlign: "center",
                                  fontWeight: 600,
                                }}
                              >
                                {attendanceDetails?.shift_out_time ?? "-"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  textAlign: "center",
                                  fontWeight: 600,
                                }}
                              >
                                {attendanceDetails?.worked_shift_duration ??
                                  "00:00"}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Approved sessions merged into the shift, e.g. an
                              On Duty that set the shift start. */}
                          {mergedSessions(attendanceDetails).map((merge) => (
                            <Box
                              key={`${merge.type}-${merge.approval_id}`}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                p: 1,
                                borderBottom: "1px solid #c8cbd3",
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600 }}
                              >
                                {mergeLabel(merge.type)}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ textAlign: "center" }}
                              >
                                {merge.merged_start_time ??
                                  merge.approved_start_time ??
                                  "-"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ textAlign: "center" }}
                              >
                                {merge.merged_end_time ??
                                  merge.approved_end_time ??
                                  "-"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ textAlign: "center" }}
                              >
                                {merge.merged_duration ?? "-"}
                              </Typography>
                            </Box>
                          ))}

                          {attendanceDetails?.shift_data?.break_data?.map(
                            (breakItem, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                  p: 1,
                                  bgcolor: "rgba(234, 171, 53, 0.1)",
                                  borderBottom: "1px solid #c8cbd3",
                                }}
                              >
                                {isTimelessBreak(breakItem) ? (
                                  // A fixed deduction has no start or end, so
                                  // its label runs across the empty time
                                  // cells and stays on one line.
                                  <>
                                    <Box
                                      sx={{
                                        gridColumn: "span 3",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 600,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        Mandatory deduction
                                      </Typography>
                                      <Tooltip
                                        title={`A mandatory break of ${
                                          breakItem.break_duration ?? "00:00"
                                        } is deducted from this shift. It has no start or end time.`}
                                        arrow
                                        slotProps={tooltipStyleProps}
                                      >
                                        <InfoOutlined
                                          sx={{
                                            fontSize: 16,
                                            color: "#9e9e9e",
                                            cursor: "pointer",
                                          }}
                                        />
                                      </Tooltip>
                                    </Box>
                                  </>
                                ) : (
                                  <>
                                    {index ===
                                    attendanceDetails?.shift_data?.break_data?.findIndex(
                                      (item) => !isTimelessBreak(item),
                                    ) ? (
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 600,
                                          gridRow: "span 3",
                                        }}
                                      >
                                        Break
                                      </Typography>
                                    ) : (
                                      <Box />
                                    )}
                                    <Typography
                                      variant="body2"
                                      sx={{ textAlign: "center" }}
                                    >
                                      {breakItem.break_start ?? "-"}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ textAlign: "center" }}
                                    >
                                      {breakItem.break_end ?? "-"}
                                    </Typography>
                                  </>
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{ textAlign: "center" }}
                                >
                                  {breakItem.break_duration}
                                  {breakItem?.is_cancelled ? " (Cancelled)" : ""}
                                </Typography>
                              </Box>
                            ),
                          )}

                          <Box
                            sx={{
                              bgcolor: themes?.primary,
                              p: 1,
                              borderRadius: "0 0 5px 5px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Typography variant="body1">
                                  Duration
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {attendanceDetails?.worked_shift_duration ??
                                    "00:00"}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Typography variant="body1">Worked</Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {attendanceDetails?.actual_worked_shift_hours ??
                                    "00:00"}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 1, mr: 1.5 }}>
                                <Typography variant="body1">Break</Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {attendanceDetails?.shift_data
                                    ?.break_duration ?? "00:00"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        {postShiftData &&
                          attendanceDetails?.post_ot_data?.length > 0 && (
                            <Box sx={{ mb: 2, border: "1px solid #c8cbd3" }}>
                              {attendanceDetails?.post_ot_data?.length > 0 && (
                                <Box sx={headerWrapperSx}>
                                  <Box sx={headerGridSx}>
                                    <Typography />
                                    <Typography sx={headerTextSx}>
                                      Start
                                    </Typography>
                                    <Typography sx={headerTextSx}>
                                      End
                                    </Typography>
                                    <Typography sx={headerTextSx}>
                                      Duration
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              <Box
                                sx={{
                                  borderBottom: "1px solid #c8cbd3",
                                  borderRadius: "5px 5px 0 0",
                                }}
                              >
                                {attendanceDetails?.post_ot_data?.map(
                                  (row, index) => (
                                    <Box
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                        p: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        OT Session 2
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          textAlign: "center",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row?.ot_start_time}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          textAlign: "center",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row?.ot_end_time}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          textAlign: "center",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row?.ot_duration}
                                      </Typography>
                                    </Box>
                                  ),
                                )}
                              </Box>

                              {attendanceDetails?.post_ot_data?.[0]?.break_data?.map(
                                (item, index) => (
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                      p: 1,
                                      bgcolor: "rgba(234, 171, 53, 0.1)",
                                      borderBottom: "1px solid #c8cbd3",
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Break
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ textAlign: "center" }}
                                    >
                                      {item?.break_start}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ textAlign: "center" }}
                                    >
                                      {item?.break_end}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ textAlign: "center" }}
                                    >
                                      {item?.break_duration}
                                      {item?.is_cancelled ? " (Cancelled)" : ""}
                                    </Typography>
                                  </Box>
                                ),
                              )}

                              <Box
                                sx={{
                                  bgcolor: "rgba(60, 115, 231, 0.05)",
                                  p: 1,
                                  borderRadius: "0 0 5px 5px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <Typography variant="body1">
                                      Duration
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {attendanceDetails?.post_ot_data[0]
                                        ?.ot_duration ?? "00:00"}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <Typography variant="body1">
                                      Worked
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {attendanceDetails?.post_ot_data[0]
                                        ?.ot_worked_duration ?? "00:00"}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{ display: "flex", gap: 1, mr: 1.5 }}
                                  >
                                    <Typography variant="body1">
                                      Break
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {attendanceDetails?.post_ot_data[0]
                                        ?.break_duration ?? "00:00"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          )}

                        <Divider />

                        <Box sx={{ ...cardBorder, p: 2, px: 2 }}>
                          {/* Row 1: Actual Shift worked | Break | Shift Total Hours */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr 1fr",
                              gap: 3,
                              mb: 2,
                              pb: 2,
                              borderBottom: "1px solid #e0e0e0",
                            }}
                          >
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "#757575", mb: 0.5 }}
                              >
                                Actual Shift worked
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, fontSize: "1.1rem" }}
                              >
                                {attendanceDetails?.actual_worked_shift_hours ??
                                  "00:00"}
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "#757575", mb: 0.5 }}
                              >
                                Break
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, fontSize: "1.1rem" }}
                              >
                                {attendanceDetails?.shift_data?.break_duration ??
                                  "00:00"}
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "#757575", mb: 0.5 }}
                              >
                                Shift Total Hours
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, fontSize: "1.1rem" }}
                              >
                                {attendanceDetails?.shift_working_duration ??
                                  "00:00"}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Row 2: Total working hours | (empty) | Total OT */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr 1fr",
                              gap: 3,
                            }}
                          >
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "#757575", mb: 0.5 }}
                              >
                                Total OT
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, fontSize: "1.1rem" }}
                              >
                                {attendanceDetails?.total_ot_worked_duration ??
                                  "00:00"}
                              </Typography>
                            </Box>

                            <Box />

                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "#757575", mb: 0.5 }}
                              >
                                Total working hours
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, fontSize: "1.1rem" }}
                              >
                                {attendanceDetails?.total_working_hours ??
                                  "00:00"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ mt: 2 }} />
                        <OffDayPostApproval
                          attendanceDetails={attendanceDetails}
                          domain={domain}
                          setOpenSidePeek={setOpenSidePeek}
                          reCallApiOffDayDuty={reCallApiOffDayDuty}
                        />
                        {otSessionData?.length > 0 && (
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Approve Overtime
                            </Typography>
                            <Typography variant="body2" color={designColors.slate600}>
                              Approve employee overtime entries to include
                              eligible hours in the payroll processing.
                            </Typography>
                            <TableContainer
                              sx={{
                                border: "1px solid #c8cbd3",
                                borderRadius: "6px",
                                mt: 0.5,
                              }}
                            >
                              <Table>
                                <TableHead>
                                  <TableRow
                                    sx={{
                                      ...tableRowSize,
                                      bgcolor: themes?.primary,
                                    }}
                                  >
                                    <TableCell width={40}></TableCell>
                                    <TableCell>Session</TableCell>
                                    <TableCell>Start Time</TableCell>
                                    <TableCell>Worked Hrs</TableCell>
                                    <TableCell>Approved Qty</TableCell>
                                    <TableCell>Status</TableCell>
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {otSessionData?.map((session, index) => (
                                    <React.Fragment key={index}>
                                      <TableRow
                                        onClick={() => {
                                          setIsUpdate(true);
                                          handleToggle(index);
                                          setSessionDetails(session);
                                          reset();
                                          setBandRows(defaultBandRows);
                                          setEditingBandId(null);
                                        }}
                                        sx={tableRowSize}
                                      >
                                        <TableCell width={40}>
                                          <ArrowRight
                                            sx={{
                                              transition: "0.3s",
                                              transform:
                                                expandedIndex === index
                                                  ? "rotate(90deg)"
                                                  : "rotate(0deg)",
                                              cusror: "pointer",
                                            }}
                                          />
                                        </TableCell>

                                        <TableCell>
                                          {session?.session_name ?? "-"}
                                        </TableCell>
                                        <TableCell>
                                          {session?.start_time ?? "-"}
                                        </TableCell>
                                        <TableCell>
                                          {session?.worked_hours ?? "-"}
                                        </TableCell>
                                        <TableCell>
                                          {session?.approved_qty ??
                                            session?.credited_qty}
                                        </TableCell>
                                        <TableCell>
                                          {session?.status ?? "-"}
                                        </TableCell>
                                      </TableRow>

                                      {expandedIndex === index && (
                                        <TableRow>
                                          <TableCell
                                            colSpan={6}
                                            sx={{ p: 2.5 }}
                                          >
                                            <Box sx={{ p: 0.2 }}>
                                              {session?.ot_work_authorization !==
                                                "Automatic" && (
                                                <>
                                                  <Typography
                                                    sx={{
                                                      fontWeight: 600,
                                                      mb: 1,
                                                      letterSpacing: "-0.3px",
                                                    }}
                                                  >
                                                    Pre Authorization
                                                  </Typography>

                                                  <Card sx={cardBorder}>
                                                    <CardContent sx={{ p: 2 }}>
                                                      <Box
                                                        sx={{
                                                          display: "grid",
                                                          gridTemplateColumns:
                                                            "repeat(2, 1fr)",
                                                          gap: "24px 80px",
                                                        }}
                                                      >
                                                        <InfoRow
                                                          label="Start Time"
                                                          value={
                                                            session?.start_time ??
                                                            "-"
                                                          }
                                                        />
                                                        <InfoRow
                                                          label="Preapproved Qty"
                                                          value={
                                                            session?.preapproved_qty ??
                                                            "-"
                                                          }
                                                        />
                                                        <InfoRow
                                                          label="Duration"
                                                          value={
                                                            session?.pre_ot_duration ??
                                                            "-"
                                                          }
                                                        />
                                                        <InfoRow
                                                          label="Total Worked"
                                                          value={
                                                            session?.total_working_hours ??
                                                            "-"
                                                          }
                                                        />
                                                      </Box>

                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          gap: 1,
                                                          mt: 2,
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="body1"
                                                          sx={{
                                                            color: "#4c5c6b",
                                                          }}
                                                        >
                                                          Pre approved by
                                                        </Typography>
                                                        <Typography
                                                          sx={{
                                                            fontWeight: 600,
                                                          }}
                                                        >
                                                          {session?.preapproved_by ??
                                                            "-"}
                                                        </Typography>
                                                        <Typography
                                                          variant="body1"
                                                          sx={{
                                                            color: "#4c5c6b",
                                                          }}
                                                        >
                                                          On
                                                        </Typography>
                                                        <Typography
                                                          sx={{
                                                            fontWeight: 600,
                                                          }}
                                                        >
                                                          {session?.preapproved_on ??
                                                            "-"}
                                                        </Typography>
                                                      </Box>
                                                    </CardContent>
                                                  </Card>
                                                </>
                                              )}

                                              <Typography
                                                sx={{
                                                  fontWeight: 600,
                                                  mb: 1,
                                                  mt: 1,
                                                  letterSpacing: "-0.3px",
                                                }}
                                              >
                                                Approval
                                              </Typography>
                                              <form
                                                onSubmit={handleSubmit(
                                                  onSubmit,
                                                )}
                                              >
                                                <Card
                                                  sx={{
                                                    border: "1px solid #c8cbd3",
                                                  }}
                                                >
                                                  <CardContent sx={{ p: 3 }}>
                                                    <Box
                                                      sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: {
                                                          xs: "minmax(0, 1fr)",
                                                          md: "repeat(2, minmax(0, 1fr))",
                                                        },
                                                        gap: {
                                                          xs: 2,
                                                          md: "24px 40px",
                                                        },
                                                      }}
                                                    >
                                                      <InfoRow
                                                        label="Consideration"
                                                        value={
                                                          session?.consideration ??
                                                          "-"
                                                        }
                                                      />
                                                      <InfoRow
                                                        label="OT Cal Based on"
                                                        value={
                                                          session?.ot_calculation_based_on ??
                                                          "-"
                                                        }
                                                      />
                                                      <InfoRow
                                                        label="Worked Hrs"
                                                        value={
                                                          session?.worked_hrs ??
                                                          "-"
                                                        }
                                                      />
                                                      <InfoRow
                                                        label="Ot Credited QTY"
                                                        value={
                                                          session?.credited_qty ??
                                                          "-"
                                                        }
                                                        tooltip="The credited quantity is calculated based on grade hours. The payment factor will be applied during payroll calculation."
                                                      />
                                                    </Box>

                                                    <Box sx={{ mt: 3 }}>
                                                      <Box
                                                        sx={{
                                                          display: "flex",
                                                          flexDirection: {
                                                            xs: "column",
                                                            md: "row",
                                                          },
                                                          alignItems: {
                                                            xs: "stretch",
                                                            md: "center",
                                                          },
                                                          gap: 2,
                                                        }}
                                                      >
                                                        <Typography
                                                          sx={{
                                                            fontWeight: 600,
                                                          }}
                                                        >
                                                          Approved Qty
                                                        </Typography>
                                                        <Box
                                                          sx={{
                                                            width: "100%",
                                                            maxWidth: {
                                                              xs: "100%",
                                                              md: 405,
                                                            },
                                                          }}
                                                        >
                                                          {session?.ot_calculation_based_on ===
                                                            "Hours" ||
                                                          session?.ot_calculation_based_on ===
                                                            "Equivalent Hours" ? (
                                                            <>
                                                              {bandRows?.length !==
                                                                0 &&
                                                              (session?.ot_calculation_based_on ===
                                                                "Hours" ||
                                                                session?.ot_calculation_based_on ===
                                                                  "Equivalent Hours") ? (
                                                                <Typography>
                                                                  {session?.approved_qty ??
                                                                    session?.credited_qty}{" "}
                                                                </Typography>
                                                              ) : (
                                                                <CustomTimePicker
                                                                  name="durationHrs"
                                                                  control={
                                                                    control
                                                                  }
                                                                  errors={
                                                                    errors
                                                                  }
                                                                  validateFn={(
                                                                    value,
                                                                  ) => {
                                                                    if (
                                                                      !value ||
                                                                      !dayjs(
                                                                        value,
                                                                      ).isValid()
                                                                    ) {
                                                                      return "Duration is required";
                                                                    }
                                                                    return true;
                                                                  }}
                                                                />
                                                              )}
                                                            </>
                                                          ) : session?.ot_calculation_based_on ===
                                                            "Shift" ? (
                                                            <CustomTextBox
                                                              selectedValue={watch(
                                                                "no_of_shifts",
                                                              )}
                                                              rules={{
                                                                ...register(
                                                                  "no_of_shifts",
                                                                  {
                                                                    required:
                                                                      "No of shift is required",
                                                                  },
                                                                ),
                                                              }}
                                                              updateDataFunction={
                                                                updateNumberOfShifs
                                                              }
                                                              placeHolder="Enter no of shifts"
                                                              errorMessage={
                                                                errors
                                                                  ?.no_of_shifts
                                                                  ?.message
                                                                  ? errors
                                                                      ?.no_of_shifts
                                                                      ?.message
                                                                  : ""
                                                              }
                                                              textBoxError={
                                                                errors.no_of_shifts
                                                                  ? true
                                                                  : false
                                                              }
                                                            />
                                                          ) : (
                                                            <CustomTextBox
                                                              selectedValue={watch(
                                                                "amount",
                                                              )}
                                                              rules={{
                                                                ...register(
                                                                  "amount",
                                                                  {
                                                                    required:
                                                                      "Amount is required",
                                                                  },
                                                                ),
                                                              }}
                                                              updateDataFunction={
                                                                updateAmount
                                                              }
                                                              placeHolder="Enter amount"
                                                              errorMessage={
                                                                errors?.amount
                                                                  ?.message
                                                                  ? errors
                                                                      ?.amount
                                                                      ?.message
                                                                  : ""
                                                              }
                                                              textBoxError={
                                                                errors.amount
                                                                  ? true
                                                                  : false
                                                              }
                                                            />
                                                          )}
                                                        </Box>
                                                      </Box>
                                                    </Box>

                                                    {(session?.ot_calculation_based_on ===
                                                      "Hours" ||
                                                      session?.ot_calculation_based_on ===
                                                        "Equivalent Hours") &&
                                                      !!bandRows.length && (
                                                        <Box sx={{ mt: 3 }}>
                                                          <Box
                                                            sx={{
                                                              border:
                                                                "1px solid #d7dce5",
                                                              borderRadius:
                                                                "14px",
                                                              overflow:
                                                                "hidden",
                                                              width: "100%",
                                                            }}
                                                          >
                                                            <Box
                                                              sx={{
                                                                display: "grid",
                                                                gridTemplateColumns:
                                                                  "minmax(110px, 1.15fr) minmax(90px, 0.85fr) minmax(100px, 0.9fr) 52px",
                                                                bgcolor:
                                                                  "#f6f8fb",
                                                                borderBottom:
                                                                  "1px solid #d7dce5",
                                                              }}
                                                            >
                                                              <Box
                                                                sx={{
                                                                  px: 2.5,
                                                                  py: 2,
                                                                  borderRight:
                                                                    "1px solid #d7dce5",
                                                                }}
                                                              >
                                                                <Typography
                                                                  sx={{
                                                                    fontWeight: 500,
                                                                    fontSize: 14,
                                                                  }}
                                                                >
                                                                  Band Name
                                                                </Typography>
                                                              </Box>
                                                              <Box
                                                                sx={{
                                                                  px: 1.5,
                                                                  py: 1.25,
                                                                  borderRight:
                                                                    "1px solid #d7dce5",
                                                                }}
                                                              >
                                                                <Typography
                                                                  sx={{
                                                                    fontWeight: 500,
                                                                    fontSize: 14,
                                                                  }}
                                                                >
                                                                  Band Factor
                                                                </Typography>
                                                              </Box>
                                                              <Box
                                                                sx={{
                                                                  px: 1.5,
                                                                  py: 1.25,
                                                                  borderRight:
                                                                    "1px solid #d7dce5",
                                                                }}
                                                              >
                                                                <Typography
                                                                  sx={{
                                                                    fontWeight: 500,
                                                                    fontSize: 14,
                                                                  }}
                                                                >
                                                                  Band Hours
                                                                </Typography>
                                                              </Box>
                                                              <Box
                                                                sx={{
                                                                  px: 0.5,
                                                                  py: 1.25,
                                                                  display:
                                                                    "flex",
                                                                  alignItems:
                                                                    "center",
                                                                  justifyContent:
                                                                    "center",
                                                                }}
                                                              >
                                                                <Typography
                                                                  sx={{
                                                                    fontWeight: 500,
                                                                    fontSize: 14,
                                                                  }}
                                                                >
                                                                  Edit
                                                                </Typography>
                                                              </Box>
                                                            </Box>

                                                            {bandRows.map(
                                                              (
                                                                band,
                                                                rowIndex,
                                                              ) => (
                                                                <Box
                                                                  key={band.id}
                                                                  sx={{
                                                                    display:
                                                                      "grid",
                                                                    gridTemplateColumns:
                                                                      "minmax(110px, 1.15fr) minmax(90px, 0.85fr) minmax(100px, 0.9fr) 52px",
                                                                    borderBottom:
                                                                      rowIndex ===
                                                                      bandRows.length -
                                                                        1
                                                                        ? "none"
                                                                        : "1px solid #d7dce5",
                                                                  }}
                                                                >
                                                                  <Box
                                                                    sx={{
                                                                      px: 1.5,
                                                                      py: 1.25,
                                                                      display:
                                                                        "flex",
                                                                      alignItems:
                                                                        "center",
                                                                      borderRight:
                                                                        "1px solid #d7dce5",
                                                                    }}
                                                                  >
                                                                    <Typography
                                                                      sx={{
                                                                        fontSize:
                                                                          {
                                                                            xs: 14,
                                                                            md: 16,
                                                                          },
                                                                        overflowWrap:
                                                                          "anywhere",
                                                                      }}
                                                                    >
                                                                      {
                                                                        band.band_name
                                                                      }
                                                                    </Typography>
                                                                  </Box>
                                                                  <Box
                                                                    sx={{
                                                                      px: 1.25,
                                                                      py: 1,
                                                                      borderRight:
                                                                        "1px solid #d7dce5",
                                                                    }}
                                                                  >
                                                                    <Typography
                                                                      sx={{
                                                                        fontSize: 15,
                                                                        py: 1,
                                                                      }}
                                                                    >
                                                                      {band.band_factor ||
                                                                        "-"}
                                                                    </Typography>
                                                                  </Box>
                                                                  <Box
                                                                    sx={{
                                                                      px: 1.25,
                                                                      py: 1,
                                                                      borderRight:
                                                                        "1px solid #d7dce5",
                                                                    }}
                                                                  >
                                                                    {editingBandId ===
                                                                    band.id ? (
                                                                      <LocalizationProvider
                                                                        dateAdapter={
                                                                          AdapterDayjs
                                                                        }
                                                                      >
                                                                        <TimePicker
                                                                          ampm={
                                                                            false
                                                                          }
                                                                          viewRenderers={{
                                                                            hours:
                                                                              renderTimeViewClock,
                                                                            minutes:
                                                                              renderTimeViewClock,
                                                                            seconds:
                                                                              renderTimeViewClock,
                                                                          }}
                                                                          views={[
                                                                            "hours",
                                                                            "minutes",
                                                                          ]}
                                                                          openTo="hours"
                                                                          value={
                                                                            band.band_hours
                                                                              ? dayjs(
                                                                                  `2000-01-01T${band.band_hours}`,
                                                                                )
                                                                              : null
                                                                          }
                                                                          onChange={(
                                                                            newValue,
                                                                          ) =>
                                                                            updateBandHours(
                                                                              band.id,
                                                                              newValue &&
                                                                                dayjs(
                                                                                  newValue,
                                                                                ).isValid()
                                                                                ? dayjs(
                                                                                    newValue,
                                                                                  ).format(
                                                                                    "HH:mm",
                                                                                  )
                                                                                : "",
                                                                            )
                                                                          }
                                                                          slotProps={{
                                                                            textField:
                                                                              {
                                                                                size: "small",
                                                                                error:
                                                                                  Boolean(
                                                                                    bandRowErrors[
                                                                                      band
                                                                                        .id
                                                                                    ]
                                                                                      ?.band_hours,
                                                                                  ),
                                                                                helperText:
                                                                                  bandRowErrors[
                                                                                    band
                                                                                      .id
                                                                                  ]
                                                                                    ?.band_hours ||
                                                                                  "",
                                                                                fullWidth: true,
                                                                              },
                                                                          }}
                                                                        />
                                                                      </LocalizationProvider>
                                                                    ) : (
                                                                      <Typography
                                                                        sx={{
                                                                          fontSize: 15,
                                                                          py: 1,
                                                                        }}
                                                                      >
                                                                        {band.band_hours ||
                                                                          "--:--"}
                                                                      </Typography>
                                                                    )}
                                                                  </Box>
                                                                  <Box
                                                                    sx={{
                                                                      px: 0.25,
                                                                      py: 1,
                                                                      display:
                                                                        "flex",
                                                                      alignItems:
                                                                        "center",
                                                                      justifyContent:
                                                                        "center",
                                                                    }}
                                                                  >
                                                                    <IconButton
                                                                      size="small"
                                                                      onClick={() =>
                                                                        handleBandEditToggle(
                                                                          band,
                                                                        )
                                                                      }
                                                                      disabled={
                                                                        savingBandId ===
                                                                          band.id ||
                                                                        session?.status ===
                                                                          "Approved" ||
                                                                        session?.status ===
                                                                          "Rejected"
                                                                      }
                                                                      sx={{
                                                                        color:
                                                                          "#5f6368",
                                                                      }}
                                                                    >
                                                                      {editingBandId ===
                                                                      band.id ? (
                                                                        <Check fontSize="small" />
                                                                      ) : (
                                                                        <Edit fontSize="small" />
                                                                      )}
                                                                    </IconButton>
                                                                  </Box>
                                                                </Box>
                                                              ),
                                                            )}
                                                          </Box>
                                                        </Box>
                                                      )}

                                                    <Box
                                                      sx={{
                                                        mt: 3,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 2,
                                                      }}
                                                    >
                                                      <Typography
                                                        sx={{
                                                          fontWeight: 600,
                                                          mb: 1,
                                                        }}
                                                      >
                                                        Add payout to
                                                      </Typography>
                                                      <RadioGroup
                                                        row
                                                        value={watch(
                                                          "payout_policy",
                                                        )}
                                                        onChange={(e) =>
                                                          setValue(
                                                            "payout_policy",
                                                            e.target.value,
                                                          )
                                                        }
                                                        sx={{ gap: "20px" }}
                                                      >
                                                        <FormControlLabel
                                                          value="Next Payrun"
                                                          control={<Radio />}
                                                          label="Next Payrun"
                                                        />
                                                        <FormControlLabel
                                                          value="Backlog"
                                                          control={<Radio />}
                                                          label="Backlog"
                                                        />
                                                      </RadioGroup>
                                                    </Box>
                                                    {session?.status !==
                                                      "Approved" &&
                                                      session?.status !==
                                                        "Rejected" && (
                                                        <Box
                                                          sx={{
                                                            mt: 3,
                                                            display: "flex",
                                                            justifyContent:
                                                              "end",
                                                            gap: 1,
                                                          }}
                                                        >
                                                          <CustomButtonWithBorder
                                                            actionName="Reject"
                                                            handleColor={true}
                                                            typeName="button"
                                                            actionFuntion={() =>
                                                              setShowConfirmationDialog(
                                                                true,
                                                              )
                                                            }
                                                            disableAction={watch(
                                                              "button_loader",
                                                            )}
                                                          />

                                                          <CustomButtonWithBorder
                                                            actionName="Approve"
                                                            typeName="submit"
                                                            disableAction={watch(
                                                              "button_loader",
                                                            )}
                                                          />
                                                        </Box>
                                                      )}
                                                  </CardContent>
                                                </Card>
                                              </form>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    </TabPanel>
                  )}
                </Box>

                <Box sx={{ mt: 1 }}>
                  <TabPanel value={tabValue} index={1}>
                    <PunchEntries
                      attendanceDetails={attendanceDetails}
                      loading={loading}
                      setTabValue={setTabValue}
                      setEditState={setEditState}
                      setRecallApi={setRecallApi}
                      shiftDetails={shiftDetails}
                    />
                  </TabPanel>
                </Box>
              </Box>
            </Box>
          </Box>
        </Drawer>
      </ThemeProvider>
      {editDetail ? (
        <EditMultipleCheckInOut
          editDetail={editDetail}
          setEditDetail={setEditDetail}
          shiftDetails={shiftDetails}
          setEditState={setEditState}
          departmentId={departmentId}
          setRecallApi={setRecallApi}
          isEditRecord={isEditRecord}
          setIsEditRecord={setIsEditRecord}
          reCallApi={reCallApi}
          setGetUpdatedData={setGetUpdatedData}
        />
      ) : null}
    </React.Fragment>
  );
};
